import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Button from '../../shared/Button';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import useAPI from '../../utils/api';
import { useSite } from '../../utils/contexts';

const NOT_STARTED_STATUSES = ['draft'];
const MINUTES_PER_WEEK = 10080;
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS = [1, 2, 3, 4, 5];
const WEEKEND = [0, 6];
const EVERY_DAY = [0, 1, 2, 3, 4, 5, 6];
const HOURS = Array.from({ length: 24 }, (_, hour) => ({
  value: hour,
  label: moment().hour(hour).minute(0).format('h:mm A'),
}));

// Campaigns only start/end on the hour - the picker has no minute control,
// so round every value to match (e.g. 12:47:00 -> 12:00:00).
const roundToHour = (value) => value.clone().minutes(0).seconds(0).milliseconds(0);

// Doesn't change during the component's life, so computed once rather than on
// every render. Falls back gracefully if Intl's timezone lookup isn't available.
const LOCAL_TIMEZONE_LABEL = (() => {
  try {
    const name = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return `${name}, GMT${moment().format('Z')}`;
  } catch (e) {
    return `GMT${moment().format('Z')}`;
  }
})();

// Converts local days-of-week + hour range into UTC "minute of week" ranges
// (0 = Sunday 00:00 UTC .. 10079 = Saturday 23:59 UTC), one per selected day.
// `.day(n)` resolves to the n-th day of the CURRENT week regardless of moment().
function localPatternToUtcRanges(daysOfWeek, localStartHour, localEndHour, fullDay) {
  return daysOfWeek.map((day) => {
    const startUtc = moment().day(day).hour(fullDay ? 0 : localStartHour).minute(0).second(0).millisecond(0).utc();
    const startMinute = startUtc.day() * 1440 + startUtc.hour() * 60 + startUtc.minute();

    // Full day: exactly 24 hours later, wrapped - avoids a second moment
    // conversion (and any DST-adjacent surprise from adding a calendar day).
    if (fullDay) {
      return { start_minute_of_week: startMinute, end_minute_of_week: (startMinute + 1440) % MINUTES_PER_WEEK };
    }

    const endUtc = moment().day(day).hour(localEndHour).minute(0).second(0).millisecond(0).utc();
    return {
      start_minute_of_week: startMinute,
      end_minute_of_week: endUtc.day() * 1440 + endUtc.hour() * 60 + endUtc.minute(),
    };
  });
}

// Reverses the conversion above for editing an existing recurring campaign -
// decodes stored UTC ranges back into the local days/hours the dialog shows.
function utcRangesToLocalPattern(ranges) {
  if (!ranges || ranges.length === 0) {
    return { daysOfWeek: [], localStartHour: 9, localEndHour: 17, fullDay: false };
  }

  const daysOfWeek = [];
  let localStartHour = 9;
  let localEndHour = 17;
  let fullDay = false;

  ranges.forEach((range) => {
    const startDay = Math.floor(range.start_minute_of_week / 1440);
    const startRemainder = range.start_minute_of_week % 1440;
    const startUtcMoment = moment.utc().day(startDay).hour(Math.floor(startRemainder / 60))
      .minute(startRemainder % 60).second(0).millisecond(0);
    const startLocalMoment = startUtcMoment.clone().local();

    let durationMinutes = range.end_minute_of_week - range.start_minute_of_week;
    if (durationMinutes < 0) durationMinutes += MINUTES_PER_WEEK;
    fullDay = durationMinutes === 1440;
    const endLocalMoment = startUtcMoment.clone().local().add(durationMinutes, 'minutes');

    daysOfWeek.push(startLocalMoment.day());
    localStartHour = startLocalMoment.hour();
    localEndHour = endLocalMoment.hour();
  });

  return { daysOfWeek: [...new Set(daysOfWeek)].sort(), localStartHour, localEndHour, fullDay };
}

// Phase 1: Duration (one-time window) or Recurring (weekly pattern) schedule.
// Doubles as the Edit dialog - start is only editable pre-launch; end/pattern
// editable until completed (then only the name stays editable).
export default function NewCampaignDialog({ onSaved, button, campaign }) {
  const API = useAPI();
  const site = useSite();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(campaign?.name || '');
  const [scheduleType, setScheduleType] = useState(campaign?.goal_type || 'duration');
  const [startsAt, setStartsAt] = useState(roundToHour(campaign ? moment(campaign.starts_at) : moment()));
  const [endsAt, setEndsAt] = useState(roundToHour(campaign ? moment(campaign.ends_at) : moment().add(1, 'day')));
  const [hasEndDate, setHasEndDate] = useState(campaign ? Boolean(campaign.ends_at) : false);
  const [daysOfWeek, setDaysOfWeek] = useState(WEEKDAYS);
  const [localStartHour, setLocalStartHour] = useState(9);
  const [localEndHour, setLocalEndHour] = useState(17);
  const [fullDay, setFullDay] = useState(false);
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(campaign);
  const isRecurring = scheduleType === 'recurring';
  const canEditStart = !isEdit || NOT_STARTED_STATUSES.includes(campaign.status);
  const canEditEnd = !isEdit || campaign.status !== 'completed';

  // This dialog stays mounted (just hidden) the whole time the page is open,
  // so the useState initializers above only ever run once, at page-load time -
  // "now" gets captured then and goes stale the longer a user waits before
  // actually clicking the button. Reset to a genuinely fresh "now" (or the
  // campaign's real values, for edit) every time the dialog actually opens.
  useEffect(() => {
    if (!open) return;

    if (campaign) {
      setName(campaign.name);
      setScheduleType(campaign.goal_type || 'duration');
      setStartsAt(roundToHour(moment(campaign.starts_at)));
      setEndsAt(roundToHour(campaign.ends_at ? moment(campaign.ends_at) : moment().add(1, 'day')));
      setHasEndDate(Boolean(campaign.ends_at));
      if (campaign.goal_type === 'recurring') {
        const pattern = utcRangesToLocalPattern(campaign.meta?.recurrence?.ranges);
        setDaysOfWeek(pattern.daysOfWeek);
        setLocalStartHour(pattern.localStartHour);
        setLocalEndHour(pattern.localEndHour);
        setFullDay(pattern.fullDay);
      }
    } else {
      setName('');
      setScheduleType('duration');
      setStartsAt(roundToHour(moment()));
      setEndsAt(roundToHour(moment().add(1, 'day')));
      setHasEndDate(false);
      setDaysOfWeek(WEEKDAYS);
      setLocalStartHour(9);
      setLocalEndHour(17);
      setFullDay(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSave = async () => {
    if (!name.trim()) {
      enqueueSnackbar('Please give the campaign a name', { variant: 'error' });
      return;
    }
    // Compared hour-to-hour (not exact "now") - otherwise picking the current
    // hour (e.g. 2pm at 2:47) would wrongly read as "in the past" just because
    // a few minutes of that hour have already elapsed.
    if (canEditStart && startsAt.isBefore(roundToHour(moment()))) {
      enqueueSnackbar('Start time can\'t be in the past', { variant: 'error' });
      return;
    }

    if (isRecurring) {
      if (daysOfWeek.length === 0) {
        enqueueSnackbar('Please pick at least one day', { variant: 'error' });
        return;
      }
      if (!fullDay && localEndHour <= localStartHour) {
        enqueueSnackbar('End time must be after the start time', { variant: 'error' });
        return;
      }
      if (canEditEnd && hasEndDate && (!endsAt || endsAt.isSameOrBefore(startsAt))) {
        enqueueSnackbar('Repeat-until date must be after the start date', { variant: 'error' });
        return;
      }
    } else if (!startsAt || !endsAt || endsAt.isSameOrBefore(startsAt)) {
      enqueueSnackbar('End date/time must be after the start date/time', { variant: 'error' });
      return;
    }

    setSaving(true);
    const cleanStartsAt = roundToHour(startsAt).toISOString();
    const payload = { name };
    if (canEditStart) payload.starts_at = cleanStartsAt;

    if (isRecurring) {
      payload.goal_type = 'recurring';
      payload.meta = { recurrence: { ranges: localPatternToUtcRanges(daysOfWeek, localStartHour, localEndHour, fullDay) } };
      if (canEditEnd) payload.ends_at = hasEndDate ? roundToHour(endsAt).toISOString() : null;
    } else {
      payload.goal_type = 'duration';
      if (canEditEnd) payload.ends_at = roundToHour(endsAt).toISOString();
    }

    const [ok, response] = isEdit
      ? await API.updateCampaign(campaign.id, payload)
      : await API.createCampaign(payload);
    setSaving(false);

    if (ok) {
      enqueueSnackbar(isEdit ? `Campaign "${name}" updated` : `Campaign "${name}" created`, { variant: 'success' });
      try {
        window.analytics?.track(isEdit ? 'Campaign Updated' : 'Campaign Created', {
          campaign_id: response.campaign.id,
          goal_type: payload.goal_type,
          site_id: site?.id,
          starts_at: payload.starts_at,
          ends_at: payload.ends_at,
          ...(isRecurring && {
            days_of_week: daysOfWeek,
            full_day: fullDay,
            daily_start_hour: fullDay ? null : localStartHour,
            daily_end_hour: fullDay ? null : localEndHour,
            has_end_date: hasEndDate,
          }),
        });
      } catch (_) {}
      setOpen(false);
      onSaved(response.campaign);
    } else {
      enqueueSnackbar(response.message || `Could not ${isEdit ? 'update' : 'create'} campaign`, { variant: 'error' });
    }
  };

  const toggleDay = (day) => {
    setDaysOfWeek((current) => (
      current.includes(day) ? current.filter((d) => d !== day) : [...current, day].sort()
    ));
  };

  return (
    <>
      {button(handleClickOpen)}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>{isEdit ? 'Edit Campaign' : 'New Campaign'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Campaign name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <RadioGroup
            row
            value={scheduleType}
            onChange={(e) => setScheduleType(e.target.value)}
            style={{ marginTop: '0.5em' }}
          >
            <FormControlLabel value="duration" control={<Radio disabled={isEdit} />} label="Duration" />
            <FormControlLabel value="recurring" control={<Radio disabled={isEdit} />} label="Recurring" />
          </RadioGroup>

          <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
            <div style={{ marginTop: '0.5em' }}>
              <DateTimePicker
                label={isRecurring ? 'Runs from' : 'Starts'}
                views={['year', 'month', 'day', 'hours']}
                closeOnSelect={false}
                format="DD/MM/YYYY hh:mm A"
                value={startsAt}
                onChange={(value) => value && setStartsAt(roundToHour(value))}
                disabled={!canEditStart}
                minDateTime={canEditStart ? roundToHour(moment()) : undefined}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="dense"
                    helperText={!canEditStart ? 'Already started - can\'t be changed' : 'Starts on the hour'}
                  />
                )}
              />
            </div>

            {!isRecurring && (
              <div style={{ marginTop: '1em' }}>
                <DateTimePicker
                  label="Ends"
                  views={['year', 'month', 'day', 'hours']}
                  closeOnSelect={false}
                  format="DD/MM/YYYY hh:mm A"
                  value={endsAt}
                  onChange={(value) => value && setEndsAt(roundToHour(value))}
                  disabled={!canEditEnd}
                  minDateTime={canEditEnd ? startsAt : undefined}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      margin="dense"
                      helperText={!canEditEnd ? 'Campaign already completed' : 'Ends on the hour'}
                    />
                  )}
                />
              </div>
            )}
          </LocalizationProvider>

          {isRecurring && (
            <>
              <div style={{ marginTop: '1em', fontSize: '13px', fontWeight: 600 }}>Repeats on</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
                {DAY_LABELS.map((label, day) => (
                  <FormControlLabel
                    key={day}
                    disabled={!canEditEnd}
                    control={
                      <Checkbox
                        size="small"
                        checked={daysOfWeek.includes(day)}
                        onChange={() => toggleDay(day)}
                      />
                    }
                    label={label}
                  />
                ))}
              </div>
              <div style={{ display: 'flex', gap: '0.5em', marginTop: '0.25em' }}>
                <Button disabled={!canEditEnd} color="white" onClick={() => setDaysOfWeek(WEEKDAYS)}>Weekdays</Button>
                <Button disabled={!canEditEnd} color="white" onClick={() => setDaysOfWeek(WEEKEND)}>Weekend</Button>
                <Button disabled={!canEditEnd} color="white" onClick={() => setDaysOfWeek(EVERY_DAY)}>Every day</Button>
              </div>

              <FormControlLabel
                style={{ marginTop: '0.5em' }}
                disabled={!canEditEnd}
                control={<Checkbox checked={fullDay} onChange={(e) => setFullDay(e.target.checked)} />}
                label="Full day (runs all day on the selected days)"
              />

              <div style={{ display: 'flex', gap: '1em', marginTop: '0.5em' }}>
                <TextField
                  select
                  fullWidth
                  label="Daily start"
                  size="small"
                  disabled={!canEditEnd || fullDay}
                  value={fullDay ? 0 : localStartHour}
                  onChange={(e) => setLocalStartHour(Number(e.target.value))}
                >
                  {HOURS.map((hour) => <MenuItem key={hour.value} value={hour.value}>{hour.label}</MenuItem>)}
                </TextField>
                <TextField
                  select
                  fullWidth
                  label="Daily end"
                  size="small"
                  disabled={!canEditEnd || fullDay}
                  value={fullDay ? 0 : localEndHour}
                  onChange={(e) => setLocalEndHour(Number(e.target.value))}
                >
                  {HOURS.map((hour) => <MenuItem key={hour.value} value={hour.value}>{hour.label}</MenuItem>)}
                </TextField>
              </div>

              <FormControlLabel
                style={{ marginTop: '0.5em' }}
                disabled={!canEditEnd}
                control={<Checkbox checked={hasEndDate} onChange={(e) => setHasEndDate(e.target.checked)} />}
                label="Stop repeating on a specific date"
              />
              {hasEndDate && (
                <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
                  <DateTimePicker
                    label="Repeats until"
                    views={['year', 'month', 'day', 'hours']}
                    closeOnSelect={false}
                    format="DD/MM/YYYY hh:mm A"
                    value={endsAt}
                    onChange={(value) => value && setEndsAt(roundToHour(value))}
                    disabled={!canEditEnd}
                    minDateTime={startsAt}
                    renderInput={(params) => <TextField {...params} fullWidth margin="dense" />}
                  />
                </LocalizationProvider>
              )}
            </>
          )}

          {/* Times above are shown in your browser's local timezone. Since not every
              account has a timezone configured, UTC is the one reference point that
              means the same thing to everyone (e.g. a team spread across timezones
              managing the same site) - shown here so it's easy to double-check
              rather than just trusting the local display silently. */}
          <div style={{ marginTop: '0.75em', fontSize: '12px', color: '#9e9e9e' }}>
            * Times chosen above use your device's local timezone ({LOCAL_TIMEZONE_LABEL}).
          </div>
          {!isRecurring && startsAt?.isValid() && endsAt?.isValid() && (
            <div style={{ marginTop: '0.25em', fontSize: '12px', color: '#9e9e9e' }}>
              UTC: {roundToHour(startsAt).utc().format('MMM D, YYYY h:mm A')} → {roundToHour(endsAt).utc().format('MMM D, YYYY h:mm A')}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="white">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} color="primary">
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
